import { Component, Watch, Ref } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';

import { getComponentProps, openModal, getModalId, closeModal } from '@/utils/vueUtils';
import SelectItem from '@/common/interfaces/SelectItem';
import { ImageSizes, ImageSize } from '@/common/interfaces/SizeBreakpoints';

import {
  BrochureRequestBlockViewModel,
  BrochureUpdateCount,
} from './BrochureRequestModule.types';
import BrochureRequestForm from './BrochureRequestForm/BrochureRequestForm.vue';
import { BrochureRequestFormModel } from './BrochureRequestForm/BrochureRequestForm.types';
import OrderSummaryLockup from './OrderSummaryLockup/OrderSummaryLockup.vue';

import StoreModuleMixin from '@/mixins/StoreModuleMixin';
import {
  BrochureRequestModuleState,
  SHOW_SUMMARY,
  BACK_TO_FORM,
  SUBMIT_BROCHURES,
  SUBMIT_INTERESTS,
  SET_SUGGESTED_ADDRESS,
  VALIDATE_ADDRESS,
  HIDE_ADDRESS_POPUP,
  UPDATE_BROCHURE_COUNT,
} from './BrochureRequestModule.store';
import { InterestsFormModel } from '@/modules/BrochureRequestModule/InterestsForm/InterestsForm.types';
import Cross from '@/assets/icons/cross.svg?inline';
import ResponsiveImage from '@/components/ResponsiveImage/ResponsiveImage.vue';
import Dropdown from '@/forms/FormElements/Inputs/Dropdown/Dropdown.vue';
import ButtonRow from '@/forms/FormElements/Rows/ButtonRow/ButtonRow.vue';
import Warning from '@/assets/icons/warning.svg?inline';
import SelectedBrochuresLockup from './SelectedBrochuresLockup/SelectedBrochuresLockup.vue';
import { getCountryName } from '@/forms/FormElements/Rows/AddressRow/AddressRow.helper';
import Address from '@/common/interfaces/dto/Address';
import { SegmentElement, SegmentTrackEvents } from '@/plugins/utm/SegmentElement';
import { getFormPayload } from '@/utils/commonUtils';
import RecaptchaMixin from '@/forms/FormElements/Mixins/RecaptchaMixin';
import Cookies from 'js-cookie';
import BrochureDeepLinkModule from '../BrochureDeepLinkModule/BrochureDeepLinkModule.vue';
import { isInUSTimezone } from 'in-us'
import { hasAcceptedAdditionalIntegrationsCookie } from '../EmailSignUpModule/EmailSignUpModule.api';
import { getCFCountry, shouldShowOptIn } from '@/store';

@Component({
  props: getComponentProps<BrochureRequestBlockViewModel>({
    IsBtB: false,
    BackLink: { Title: 'Back to all brochures', Link: '#' },
    SelectedBrochures: [{ Id: 1, Destinations: [1, 12], Title: 'Alaska 2020-21 Brochure', Image: { Url: '#', Alt: '' }, Code: "ANTA", IsActive: true, DirectMail: true }],
    MoreBrochures: [{ Id: 1, Destinations: [1, 12], Title: 'Alaska 2020-21 Brochure', Image: { Url: '#', Alt: '' }, Code: "ANTA", IsActive: true, DirectMail: true }],
    BrochureThankYou: '<p>Thank you</p>',
    InterestsThankYou: '<p>Thank you</p>',
  }),
  components: {
    ResponsiveImage,
    Dropdown,
    ButtonRow,
    Cross,
    Warning,
    SelectedBrochuresLockup,
    OrderSummaryLockup,
    BrochureRequestForm,
    BrochureDeepLinkModule,
  },
})
export default class BrochureRequestModule extends mixins(StoreModuleMixin, RecaptchaMixin) {
  readonly p!: BrochureRequestBlockViewModel;
  state!: BrochureRequestModuleState;
  modalId = getModalId(this.uuid);

  brochuresPayload: any = {}
  interestsPayload = {}

  isMounted = false

  @Ref('brochureRequestForm') readonly brochureRequestForm!: BrochureRequestForm;

  get getInitialModel() {
    if (this.state.IsFilledOnce) return this.state.BrochureRequestFormModel;
    return this.p.PrefilledModel;
  }

  imageSizes: ImageSizes = {
    def: new ImageSize([144, 144]),
    sizes: {
      md: new ImageSize([164, 164]),
    },
  };

  created() {
    this.siteKey = '6Leob6kZAAAAAC1T9nc22cyh8vaqbcUHhV1vPloH';
  }
 
  mounted() {
    if (this.state) {
      const allActive = this.state.SelectedBrochures.every(item => item.IsActive);
      const allDirectMail = this.state.SelectedBrochures.every(item => item.DirectMail);
      if (!allActive || (allActive && !allDirectMail)) {
        window.location.href = "/brochures/";
      }
    }

    SegmentElement.sendEvent('formViewed', {
      ...getFormPayload('BrochureRequestForm', this.$attrs.contentLinkId),
      nonInteraction: 1,
    })
    this.isMounted = true;
  }

  get SelectedBrochures() {
    return this.state.SelectedBrochures;
  }

  get BrochureCardProps() {
    if (this.state.BrochureCardData) {
      const brochure = this.state.BrochureCardData[0]
      brochure.DirectMail = false
      return {
        Brochures: [brochure]
      }
    }
  }

  get hints() {
    return this.getLinesForPopup(this.state.AddressValidatorModel);
  }

  get moreBrochuresList(): SelectItem[] {
    if (!this.p.MoreBrochures) return null;
    return this.p.MoreBrochures.map((b) => ({ Value: b.Id, Text: b.Title }));
  }

  get enteredAddressLines() {
    return this.getLinesForPopup(this.state.BrochureRequestFormModel.Address);
  }

  getLinesForPopup(address: Address) {
    return [
      getCountryName(+address.CountryId),
      address.Street1,
      address.Street2,
      `${address.City}, ${address.StateCode}, ${address.PostalCode}`,
    ].filter((l) => l);
  }

  get getAddressLines() {
    const address = this.state.BrochureRequestFormModel.Address;
    return [address.Street1, address.Street2, address.StateCode, address.PostalCode];
  }

  submitBrochureForm(model: BrochureRequestFormModel) {
    if (this.state.IsAddressValidated) {
      this.showSummary(model);
    } else {
      this.validateAddress(model);
    }
  }

  async submitBrochures() {
    const model = this.state.BrochureRequestFormModel;
    model.IsSubscribe = true;
    model.RecaptchaResponse = await this.getRecaptchaToken();
    this.brochuresPayload = {
      brochure_id: String(model.SelectedBrochures[0].Id),
      brochure_name: model.SelectedBrochures[0].Title,
      ...getFormPayload('BrochureRequestForm', this.$attrs.contentLinkId)
    };
    SegmentElement.sendEvent('formSubmissionAttempted', getFormPayload('BrochureRequestForm', this.$attrs.contentLinkId))
    const referring_domain = Cookies.get('referring_domain')
    const natgeo_disney_marketing_optin = model.DisneyOptin !== undefined ? model.DisneyOptin : null;
    var serverPayload = {
      ...this.brochuresPayload,
      brochure_code: model.SelectedBrochures[0].Code,
      quantity: 1,
      ...(!SegmentElement.getIsLanding() && { previous_page_name: SegmentElement.getPrevPageName() }),
      page_name: SegmentElement.getPageName(),
      ...(model.Person.Title && { title: model.Person.Title }),
      first_name: model.Person.FirstName,
      last_name: model.Person.LastName,
      email: model.Person.Email,
      phone: `+${model.Phone.Code}${model.Phone.Number}`,
      locale: SegmentElement.getLocale(),
      address: 
        {
          address_first: model.Address.Street1,
          address_second: model.Address.Street2,
          city: model.Address.City,
          country_code: model.Address.CountryName,
          postal_code: model.Address.PostalCode,
          state: model.Address.StateCode,
        }
      ,
      ...(referring_domain && { referring_domain }),
      ...(model.IsPastGuest && { past_guest: model.IsPastGuest }),
      ...(model.IsTravelAgent && { travel_agent: model.IsTravelAgent }),
      ...(Cookies.get('_fbc') && { fbclid: Cookies.get('_fbc').split('.')[3] }),
      ...(Cookies.get('email_md5') && { email_md5: Cookies.get('email_md5') }),
      ...(Cookies.get('_uetmsclkid') && { mscklid: Cookies.get('_uetmsclkid').split('_uet')[1] }),
      ...(Cookies.get('_gcl_aw') && { gclid: Cookies.get('_gcl_aw').split('.')[2] }),
      ...(shouldShowOptIn() && { eea_uk_optin: true }),
      natgeo_disney_marketing_optin,
      country_code: getCFCountry(),
    };
    const res = { ...model, AnalyticsPayload: JSON.stringify(serverPayload) };
    this.postBrochures(res)
  }

  get isBrochuresSubmitted() {
    return this.state?.BrochureRequestId;
  }

  @Watch('isBrochuresSubmitted')
  postBrochuresDone(){
    Cookies.set('newsletterSubscribed', true, { expires: 365 });

    const hasAccepted = hasAcceptedAdditionalIntegrationsCookie()
    if (hasAccepted === true || (typeof hasAccepted === 'undefined' && isInUSTimezone())) {
      const script = document.createElement('script')
      script.src = `//cdn.datasteam.io/js/D2CA27E6C1BB02.js?conversion=true`
      document.head.appendChild(script)
    }

    this.brochuresPayload.transaction_id = this.state.BrochureRequestId;
    SegmentElement.sendEvent(SegmentTrackEvents.formSubmitted, this.brochuresPayload)
  }

  get isOpenPopup() {
    return this.state?.IsShowAddresPopup;
  }

  get shouldChangeAddress() {
    return this.state?.ShouldChangeAddress;
  }

  @Watch('isOpenPopup')
  isOpenPopupChange() {
    if (this.state.IsShowAddresPopup) {
      openModal(this.$modal, this.modalId);
    } else {
      this.closeModal();
    }
  }

  @Watch('shouldChangeAddress')
  isAddressChanges() {
    if (this.shouldChangeAddress && this.state.BrochureRequestFormModel?.Address) {
      var addressFormModel = (this.$refs.brochureRequestForm as BrochureRequestForm).$data.model;
      addressFormModel.Address.Street1 = this.state.BrochureRequestFormModel.Address.Street1;
      addressFormModel.Address.Street2 = this.state.BrochureRequestFormModel.Address.Street2;
      addressFormModel.Address.City = this.state.BrochureRequestFormModel.Address.City;
      addressFormModel.Address.StateCode = this.state.BrochureRequestFormModel.Address.StateCode;
      addressFormModel.Address.CountryId = this.state.BrochureRequestFormModel.Address.CountryId;
      addressFormModel.Address.PostalCode = this.state.BrochureRequestFormModel.Address.PostalCode;
    }
  }

  closeModal() {
    this.hideAddressPopup();
    closeModal(this.$modal, this.modalId);
  }

  showSummary: (model: BrochureRequestFormModel) => void = this.getAction(SHOW_SUMMARY);
  backToForm: () => void = this.getAction(BACK_TO_FORM);
  postBrochures: (payload: any) => void = this.getAction(SUBMIT_BROCHURES);
  postInterests: (model: InterestsFormModel) => void = this.getAction(SUBMIT_INTERESTS);
  validateAddress: (model: BrochureRequestFormModel) => void = this.getAction(VALIDATE_ADDRESS);
  hideAddressPopup: () => void = this.getAction(HIDE_ADDRESS_POPUP);
  setNewAddress: (shouldSetNew: boolean) => void = this.getAction(SET_SUGGESTED_ADDRESS);
  updateBrochureCount: (item: BrochureUpdateCount) => void = this.getMutation(UPDATE_BROCHURE_COUNT,);
}
